/**
 * Interface icons: navigation, view controls and menus, from Phosphor
 * (@phosphor-icons/react), regular weight, in `currentColor` so they take the
 * control's colour and state. Decorative: every control that shows one names
 * itself in text or an accessible label.
 *
 * Fluidic element symbols are not icons: they are drawn by render/glyphs.tsx
 * from the scene model, and stay there.
 *
 * Named by what they mean in Fluidea rather than by Phosphor's names, so a
 * glyph can be swapped here without touching the controls that use it.
 */
import {
  Archive,
  ArrowCounterClockwise,
  ArrowUUpLeft,
  ArrowUUpRight,
  Books,
  CaretDown,
  Copy,
  FolderSimple,
  PencilSimple,
  Plus,
  Trash,
  CaretLeft,
  CaretRight,
  Cursor,
  LineSegment,
  Note,
  TextT,
  CornersOut,
  Drop,
  Export,
  GearSix,
  ListChecks,
  Pulse,
  Package,
  Ruler,
  SelectionBackground,
  SignOut,
  SlidersHorizontal,
  SquaresFour,
  Stack,
  Tag,
  UsersThree,
  Wind,
  type Icon as PhosphorIcon,
} from "@phosphor-icons/react";

const SIZE = 22;

function icon(Glyph: PhosphorIcon) {
  return function FluideaIcon({ size = SIZE }: { size?: number }) {
    return <Glyph size={size} weight="regular" aria-hidden="true" focusable="false" />;
  };
}

/** Projects and their designs. */
export const ProjectsIcon = icon(SquaresFour);
/** Programmes group projects. */
export const ProgrammesIcon = icon(Stack);
/** The shared library. */
export const LibraryIcon = icon(Books);
/** The activity log. */
export const ActivityIcon = icon(Pulse);
/** Background jobs. */
export const JobsIcon = icon(ListChecks);
/** Settings. */
export const SettingsIcon = icon(GearSix);
/** Sign out. */
export const SignOutIcon = icon(SignOut);
/** Zoom to the extents of the drawing. */
export const ZoomExtentsIcon = icon(CornersOut);
/** Component labels. */
export const LabelsIcon = icon(Tag);
/** Channel lengths. */
export const DimensionsIcon = icon(Ruler);
/** Fluid lines. */
export const FluidLinesIcon = icon(Drop);
/** Air lines (D30). */
export const AirLinesIcon = icon(Wind);
/** Export the drawing as an image. */
export const ExportIcon = icon(Export);
/** The select tool. */
export const SelectToolIcon = icon(Cursor);
/** The connector tool: a line between two ports. */
export const ConnectorToolIcon = icon(LineSegment);
/** Undo and redo: U-turn arrows, which do not read as rotation the way curved arrows do. */
export const UndoIcon = icon(ArrowUUpLeft);
export const RedoIcon = icon(ArrowUUpRight);
/** The wash over the consumable: everything inside the external interfaces. */
export const InsideIcon = icon(Package);
/** The wash over everything outside the external interfaces. */
export const OutsideIcon = icon(SelectionBackground);
/** Accounts, for administrators (D37). */
export const UsersIcon = icon(UsersThree);
/** Placement defaults (D36): what new components and lines start with. */
export const DefaultsIcon = icon(SlidersHorizontal);
/** The caret on a side panel's fold tab: which way the panel will move. */
export const PanelCaretLeftIcon = icon(CaretLeft);
export const PanelCaretRightIcon = icon(CaretRight);
/** Edit a row: an account, for administrators. */
export const EditIcon = icon(PencilSimple);
/** Add something new: an account, a project. */
export const AddIcon = icon(Plus);
/** Permanent deletion (D33). */
export const DeleteIcon = icon(Trash);
/** Archive a project: out of the list, nothing removed (D38). */
export const ArchiveIcon = icon(Archive);
/** Restore an archived project. */
export const RestoreIcon = icon(ArrowCounterClockwise);
/** A section that folds open and shut. Rotated by CSS when shut. */
export const DisclosureIcon = icon(CaretDown);
/** Move a project to another programme. */
export const MoveIcon = icon(FolderSimple);
/** Start a new design from this one: copy or fork. */
export const CopyIcon = icon(Copy);
/** The note tool: click to write a note on the schematic. */
export const NoteToolIcon = icon(TextT);
/** Notes shown or hidden, in the view controls. */
export const NotesIcon = icon(Note);
